import { useEffect, useMemo, useState } from "react";
import {
  AccountData,
  Event,
  ProfileContactPageId,
  ProfilePage,
  Visibility,
} from "@markit/common.types";
import { useSelector, useDispatch } from "react-redux";
import {
  accountActions,
  getAccountState,
  LoginState,
} from "../redux/slices/accountSlice";
import { isEventExternalLink } from "@markit/common.utils";
import { isProfileRCA } from "../utils/globalUtils";
import { API } from "../API";
import useAsyncOnMount from "./useAsyncEffectOnMount";
import { fetchEventOrganizers } from "../utils/eventUtils/eventUtils";
import { getEventState } from "../redux/slices/eventSlice";
import { getUserProfilePagesSnap } from "../utils/FirebaseUtils";

type useProfilePageFullDataProps = {
  userData: AccountData;
};

export const useProfilePageFullData = (props: useProfilePageFullDataProps) => {
  const { userData } = props;
  const { accountData, loggedIn, appInitialized } =
    useSelector(getAccountState).account;
  const { events: eventList, tickets } = useSelector(getEventState).events;
  const dispatch = useDispatch();
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [userAttendingEvents, setUserAttendingEvents] = useState<Event[]>([]);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [eventOrganizers, setEventOrganizers] = useState<AccountData[]>([]);
  const [tabItem, setTabItem] = useState("");
  const [profilePages, setProfilePages] = useState<ProfilePage[]>([]);

  const isUserRCA = useMemo(
    () => (userData && isProfileRCA(userData.uid)) ?? false,
    [userData]
  );

  const isMyAccount = useMemo(
    () =>
      loggedIn === LoginState.LOGGED_IN && accountData.uid === userData?.uid,
    [accountData.uid, loggedIn, userData?.uid]
  );

  // Filter out draft events (if not viewing own profile)
  const ownVisibleProducts = useMemo(() => {
    return eventList.filter((event) => !event.isDraft);
  }, [eventList]);

  useAsyncOnMount(async () => {
    let finalEvents: Event[] = [];
    if (loadingEvents) {
      if (isMyAccount) {
        // Return early if redux state is not loaded yet
        if (!appInitialized) {
          return;
        }
        // For my account, show all events, including attending/attended events
        // nonCreatedTickets refers to any event you aren't the explicit creator
        const nonCreatedTickets = tickets.filter(
          (ticket) => ticket.customTicketId !== ""
        );
        const nonCreatedEvents = ownVisibleProducts
          .filter((event) => !isEventExternalLink(event.eventType))
          .filter((myEvent: Event) =>
            nonCreatedTickets.find((tickets) => tickets.eventId === myEvent.id)
          );
        setUserAttendingEvents(
          nonCreatedEvents.sort((a, b) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          })
        );
        setUserEvents(ownVisibleProducts);
        finalEvents = ownVisibleProducts;
      } else {
        // For other profiles, only show the upcoming and past events, not attending/attended events
        const otherWishlistResponse = await API.user.otherUserWishList({
          uid: userData.uid,
        });
        const { events } = otherWishlistResponse;
        setUserEvents(
          events.filter(
            (event) => !event.isDraft && event.visibility === Visibility.Public
          )
        );
        finalEvents = events;
      }

      const eventOrganizers = await fetchEventOrganizers(finalEvents);
      setEventOrganizers(eventOrganizers);
    }

    setLoadingEvents(false);
    dispatch(accountActions.setIsRCA(isUserRCA));
  });

  // Whenever a new event is created while on your own profile page, this will add any newly created events
  useEffect(() => {
    if (!loadingEvents && isMyAccount) {
      setUserEvents(ownVisibleProducts);
    }
  }, [ownVisibleProducts, loadingEvents, isMyAccount]);

  useAsyncOnMount(async () => {
    const profilePagesSnap = await getUserProfilePagesSnap(userData.uid);
    const profilePages = profilePagesSnap.docs.map((doc) => doc.data());

    // If not in creator mode and all profile pages have no sections, should not show the pages
    if (
      !accountData.inCreatorMode &&
      profilePages.every((page) => page.sections.length === 0)
    ) {
      return;
    }

    // Show the profile pages if any is true:
    // 1. The page is the contact page
    // 2. The user is viewing own account in creator mode
    // 3. The page has sections
    const sortedPages = profilePages
      .filter(
        (page) =>
          page.id === ProfileContactPageId ||
          (isMyAccount && accountData.inCreatorMode) ||
          page.sections.length > 0
      )
      .sort(
        (a, b) =>
          userData.profilePagesOrder.indexOf(a.id) -
          userData.profilePagesOrder.indexOf(b.id)
      );
    setProfilePages(sortedPages);

    if (sortedPages.length > 0) {
      setTabItem(sortedPages[0].id);
    }
  });

  useEffect(() => {
    // Reset the tab item to the first page if the current tab item no longer exists
    if (
      profilePages.length > 0 &&
      !profilePages.some((page) => page.id === tabItem)
    ) {
      setTabItem(profilePages[0].id);
    }
  }, [profilePages, tabItem]);

  return {
    loadingEvents,
    userAttendingEvents,
    userEvents,
    eventOrganizers,
    tabItem,
    setTabItem,
    profilePages,
    setProfilePages,
  };
};
